import maxBy from 'lodash/maxBy'
import cloneDeep from 'lodash/cloneDeep'
import type { GameItem } from '../lobby-games/interface'
import type { BigJackpot, GameSlotJackpotResponseData, TopWinner } from '~/stores/jackpot.store'
import type { JackpotBanner } from '~/composables/common/interface'
import { useJackpotStore } from '~/stores/jackpot.store'
import { useApi } from '~/composables/common/use-api'
import { GAMES_API } from '~/configs/api'
import { HttpRequestMethod } from '~/configs/enums/http'

export function useJackPot() {
  const allGameList = ref<GameItem[]>([])
  const storeJackpot = useJackpotStore()
  const { request } = useApi()

  const topWinners = computed((): TopWinner[] => {
    return storeJackpot.getTopWinners()
  })

  const bigJackpots = computed((): BigJackpot[] => {
    return storeJackpot.getBigJackpot()
  })

  const jackpots = computed((): GameSlotJackpotResponseData => {
    return storeJackpot.getJackpot()
  })

  const slotsJackpot = computed(
    () => Object.entries(jackpots.value)
      .reduce((sum, [gameId, jackpot]) => gameId.includes('kts') ? sum + jackpot : sum, 0)
  )

  async function getGameTopWinners(): Promise<void> {
    try {
      const { data } = await request(GAMES_API.TOP_WINNERS, {
        method: HttpRequestMethod.GET
      })
      if (data) {
        const topWinnersList = cloneDeep(data)

        const { data: allGameData } = await request(GAMES_API.GAMES, {
          method: HttpRequestMethod.GET
        })
        if (allGameData) {
          allGameList.value = allGameData
          storeJackpot.setTopWinners(topWinnersList[0]?.data.summary?.map((item: TopWinner) => ({
            ...item,
            recent_game: allGameData[Math.floor(Math.random() * allGameData.length)]
          })) ?? [])
        } else {
          storeJackpot.setTopWinners(topWinnersList[0]?.data.summary?.map((item: TopWinner) => ({
            ...item,
            recent_game: item.recent_game
          })) ?? [])
        }
      }
    } catch { /* empty */ }
  }

  async function getGameJackpotsList(): Promise<void> {
    try {
      const { data } = await request(GAMES_API.JACKPOT, {
        method: HttpRequestMethod.GET
      })
      if (data) {
        const jackpotNumber = cloneDeep(data)
        const { big_jackpot } = jackpotNumber
        if (big_jackpot) {
          storeJackpot.setBigJackpot(big_jackpot)
        }
        delete jackpotNumber.big_jackpot
        storeJackpot.setJackpot(jackpotNumber)
        if (data.big_jackpot) {
          const jackpotBanner = data.big_jackpot as JackpotBanner[]
          const gameItem: JackpotBanner | undefined = maxBy(
            jackpotBanner.filter(item => item.on),
            'currentValue'
          )
          if (gameItem) {
            storeJackpot.setXocDiaJackpot({
              gameId: gameItem.id,
              gameName: gameItem.name,
              value: gameItem.currentValue
            })
            return
          }
          storeJackpot.setXocDiaJackpot({
            gameId: '',
            gameName: '',
            value: 0
          })
        }
      }
    } catch { /* empty */ }
  }

  function handleJackpotNumber(gameId: string | undefined): number {
    if (!jackpots.value || !gameId) {
      return 0
    }
    return jackpots.value[gameId.replace('_', '')]
      || Object.entries(jackpots.value).find(([id]) => gameId.includes(id))?.[1]
      || 0
  }

  return {
    allGameList,
    bigJackpots,
    jackpots,
    slotsJackpot,
    topWinners,
    getGameTopWinners,
    handleJackpotNumber,
    getGameJackpotsList
  }
}
